/**
 * UI helpers and presentation logic for the gallery builder component.
 */
export const GalleryBuilderUI = {
  layout_dropdown_open: false,
  mobile_initial_state: true,
  mobile_active_artwork_index: 0,
  search_open: false,
  search_query: '',

  handleMobileInteraction() {
    if (this.mobile_initial_state) {
      this.mobile_initial_state = false;
      this.layout_dropdown_open = false;
      if (this.$store.galleryUI) {
        this.$store.galleryUI.user_has_interacted = true;
      }
    }
  },

  selectMobileArtwork(index) {
    const maxIndex = this.$store.galleryCore.selected_artworks.length - 1;
    this.mobile_active_artwork_index = Math.min(index, maxIndex);
    this.handleMobileInteraction();

    const selectedArtwork = this.$store.galleryCore.selected_artworks[this.mobile_active_artwork_index];
    if (selectedArtwork) {
      // Update immediately since infinite is disabled
      this.updateMobileCarouselForSelectedArtwork(selectedArtwork.id);
      this.initializeSwymWishlist();
    }
  },

  validateMobileArtworkIndex() {
    const maxIndex = this.$store.galleryCore.selected_artworks.length - 1;
    if (this.mobile_active_artwork_index > maxIndex) {
      this.mobile_active_artwork_index = Math.max(0, maxIndex);
    }
  },

  isMobile() {
    return window.innerWidth <= 989;
  },

  get mobileHeaderHeight() {
    if (!this.isMobile()) return 0;

    let height = 48 + 56;

    if (!this.mobile_initial_state) {
      height += 88;
    }

    return height;
  },

  get displayed_artworks() {
    const previewStore = this.$store.galleryPreview;

    // Mobile preview mode: show ALL artworks for landscape viewing
    if (previewStore?.mobile_preview_active) {
      return this.$store.galleryCore.selected_artworks;
    }

    if (!this.isMobile()) {
      return this.$store.galleryCore.selected_artworks;
    }

    const activeArtwork = this.$store.galleryCore.selected_artworks[this.mobile_active_artwork_index];
    return activeArtwork ? [activeArtwork] : [];
  },

  getArtworkImageUrl(artwork, type = 'display') {
    if (!artwork?.id) return '';
    const urls = this.$store.galleryCore.getArtworkUrls?.(artwork);

    // Mobile display: use smaller image for 268px display
    if (type === 'display' && this.isMobile()) {
      const baseUrl = urls?.baseUrl || artwork?.featured_image?.src;
      if (baseUrl) {
        const cleanUrl = baseUrl.split('?')[0];
        // 2x for retina: 268px * 2 = 536px
        return `${cleanUrl}?width=536`;
      }
    }

    if (type === 'zoom' && urls?.zoom) {
      return urls.zoom;
    }

    return urls?.[type] || artwork?.[`${type}_url`] || artwork?.image || '';
  },

  get artworkImageDimensions() {
    if (this.isMobile()) {
      return { width: 268, height: 268 };
    }
    return {
      width: this.$store.galleryCore.orientation === 'vertical' ? 315 : 436,
      height: 436
    };
  },

  getArtworkContainerClasses(index) {
    const { active_artwork_index, hover_artwork_index } = this.ui_state;
    return {
      'gallery-artwork-container--active': active_artwork_index === index,
      'gallery-artwork-container--inactive': this.isArtworkInactive(index),
      'gallery-artwork-container--hovered': hover_artwork_index === index
    };
  },

  isArtworkInactive(index) {
    const { active_artwork_index, hover_artwork_index } = this.ui_state;
    return (
      (active_artwork_index !== null && active_artwork_index !== index) ||
      (hover_artwork_index !== null && hover_artwork_index !== index)
    );
  },

  getArtworkAriaLabel(artwork, index) {
    if (!artwork) return `Empty position ${index + 1}`;
    const title = artwork.title || 'Untitled';
    const artist = artwork.artist || 'Unknown Artist';
    return `Select ${title} by ${artist} for position ${index + 1}`;
  },

  get randomizeButtonText() {
    return this.ui_state.randomizing
      ? (window.galleryTranslations?.status?.randomizing || 'Randomizing...')
      : (window.galleryTranslations?.buttons?.randomize || 'Randomize');
  },

  getThirdArtworkStyle(index) {
    if (index !== 2 || this.third_artwork_visible) return '';
    return 'display: block; opacity: 0; pointer-events: none; transition: opacity var(--gallery-fade-transition);';
  },

  getThumbnailUrl(artwork) {
    if (!artwork?.id) return '';

    const urls = this.$store.galleryCore?.getArtworkUrls?.(artwork);

    if (urls?.thumbnail) {
      return urls.thumbnail;
    }

    if (urls?.baseUrl) {
      return `${urls.baseUrl.split('?')[0]}?width=300`;
    }

    return artwork?.thumbnail_url || '';
  },

  getThumbnailSrcset(artwork) {
    if (!artwork?.id) return '';
    const url = this.getThumbnailUrl(artwork);
    return url ? `${url} 2x` : '';
  },

  /**
   * Get appropriate image URL for mobile artwork display
   * Uses larger size for initial state, smaller for header state
   */
  getMobileArtworkUrl(artwork) {
    if (!artwork?.id) return '';
    const urls = this.$store.galleryCore.getArtworkUrls?.(artwork);

    if (this.mobile_initial_state) {
      const baseUrl = urls?.baseUrl || artwork?.featured_image?.src;
      if (baseUrl) {
        const cleanUrl = baseUrl.split('?')[0];
        return `${cleanUrl}?width=360`;
      }
    }

    return urls?.thumbnail || artwork?.thumbnail_url || '';
  },

  getMobileArtworkSrcset(artwork) {
    if (!artwork?.id) return '';
    const urls = this.$store.galleryCore.getArtworkUrls?.(artwork);
    const baseUrl = urls?.baseUrl || artwork?.featured_image?.src;

    if (!baseUrl) return '';
    const cleanUrl = baseUrl.split('?')[0];

    if (this.mobile_initial_state) {
      return `${cleanUrl}?width=180 1x, ${cleanUrl}?width=360 2x`;
    }

    return `${cleanUrl}?width=80 1x, ${cleanUrl}?width=160 2x`;
  },

  initializeLoadingMessages() {
    const messages_script = this.$el.querySelector('[data-loading-messages]');
    this.$store.galleryUI.initializeLoadingMessages(messages_script);
  },

  startLoadingScreen() {
    this.initializeLoadingMessages();
    this.$store.galleryUI.startLoadingScreen();
  },

  async stopLoadingScreen() {
    await this.$store.galleryUI.stopLoadingScreen();
  },

  updateLoadingProgress(target) {
    this.$store.galleryUI.target_progress = target;
  },

  async handleArtworkPreviewClick(index) {
    if (!this.$store.galleryPreview.preview_mode) {
      return;
    }

    if (this.$store.galleryPreview.zoom_active) {
      return;
    }

    const artwork = this.$store.galleryCore.selected_artworks[index];
    if (!artwork) return;

    const storedUrls = this.$store.galleryCore.getArtworkUrls?.(artwork);

    let imageUrl = '';
    if (storedUrls?.zoom) {
      imageUrl = storedUrls.zoom;
    } else if (storedUrls?.large) {
      imageUrl = storedUrls.large;
    } else if (storedUrls?.display) {
      imageUrl = storedUrls.display;
    } else if (artwork.featured_image) {
      imageUrl = artwork.featured_image;
    } else if (artwork.image) {
      imageUrl = artwork.image;
    } else if (artwork.images && artwork.images.length > 0) {
      imageUrl = artwork.images[0].src || artwork.images[0];
    }

    if (imageUrl) {
      const [baseUrl] = imageUrl.split('?');
      imageUrl = baseUrl;

      if (imageUrl.startsWith('//')) {
        imageUrl = 'https:' + imageUrl;
      }
    } else {
      return;
    }

    if (this.$store.galleryPreview?.ensureImagePreloaded) {
      this.$store.galleryPreview.ensureImagePreloaded(imageUrl);
    }

    const imageData = {
      url: imageUrl,
      title: artwork.title || 'Untitled',
      artist: artwork.vendor || artwork.artist || 'Unknown Artist',
      orientation: this.detectArtworkOrientation(artwork)
    };

    // Initialize preview manager if needed
    if (!this.preview_manager) {
      const { galleryPreviewManager } = await import('./gallery-preview-manager.js');
      this.preview_manager = galleryPreviewManager;
      await this.preview_manager.initialize();
    }

    // Open zoom through preview manager
    await this.preview_manager.openZoom(index, imageData);
  },

  detectArtworkOrientation(artwork) {
    if (artwork.orientation) return artwork.orientation;

    if (artwork.image_width && artwork.image_height) {
      return artwork.image_width > artwork.image_height ? 'horizontal' : 'vertical';
    }

    if (artwork.tags) {
      // Handle tags as either string or array
      const tagsString = Array.isArray(artwork.tags)
        ? artwork.tags.join(' ').toLowerCase()
        : String(artwork.tags).toLowerCase();

      if (tagsString.includes('landscape') || tagsString.includes('horizontal')) return 'horizontal';
      if (tagsString.includes('portrait') || tagsString.includes('vertical')) return 'vertical';
    }

    return this.$store.galleryCore.orientation || 'horizontal';
  },

  /**
   * Check if artwork is horizontal orientation (returns boolean)
   * Used for determining which placeholder image to show
   */
  isArtworkHorizontalOrientation(artwork) {
    if (!artwork) return true; // Default to horizontal

    // Check tags for orientation
    if (artwork.tags) {
      const tagsArray = Array.isArray(artwork.tags) ? artwork.tags :
                        (typeof artwork.tags === 'string' ? [artwork.tags] : []);

      for (const tag of tagsArray) {
        if (tag === 'Orientation|Horizontal' || tag === 'orientation-horizontal' ||
            (typeof tag === 'string' && (tag.includes('Orientation|Horizontal') || tag.includes('orientation-horizontal')))) {
          return true;
        }
        if (tag === 'Orientation|Vertical' || tag === 'orientation-vertical' ||
            (typeof tag === 'string' && (tag.includes('Orientation|Vertical') || tag.includes('orientation-vertical')))) {
          return false;
        }
      }
    }

    // Fallback: check image dimensions if available
    if (artwork.featured_image?.width && artwork.featured_image?.height) {
      return artwork.featured_image.width > artwork.featured_image.height;
    }

    if (artwork.image_width && artwork.image_height) {
      return artwork.image_width > artwork.image_height;
    }

    // Default to horizontal
    return true;
  },

  initializeSwymWishlist() {
    if (typeof window._swat?.initializeActionButtons === 'function') {
      setTimeout(() => {
        window._swat.initializeActionButtons('.gallery-builder');
      }, 50);
    } else {
      this.waitForSwymAndInitialize();
    }
  },

  waitForSwymAndInitialize() {
    let attempts = 0;
    const maxAttempts = 20;
    const checkInterval = 500;

    const checkForSwym = () => {
      attempts++;

      if (window._swat && typeof window._swat.initializeActionButtons === 'function') {
        setTimeout(() => {
          window._swat.initializeActionButtons('.gallery-builder');
        }, 100);
        return;
      }

      if (attempts < maxAttempts) {
        setTimeout(checkForSwym, checkInterval);
      }
    };

    checkForSwym();
  },

  toggleSearchInput() {
    this.search_open = !this.search_open;
    if (this.search_open) {
      // Use setTimeout(0) instead of $nextTick for better mobile keyboard support
      // Mobile browsers require focus() to be called within the same event loop as user interaction
      setTimeout(() => {
        const input = this.$el.querySelector('.gallery-builder__mobile-search-input');
        if (input) input.focus();
      }, 0);

      setTimeout(() => {
        this.addSearchClickOutsideListener();
      }, 100);
    } else {
      this.removeSearchClickOutsideListener();
    }
  },

  addSearchClickOutsideListener() {
    this.searchClickOutsideHandler = (event) => {
      const searchButton = event.target.closest('.gallery-builder__mobile-filter-btn--search');
      if (!searchButton && this.search_open) {
        this.search_open = false;
        this.removeSearchClickOutsideListener();
      }
    };
    document.addEventListener('click', this.searchClickOutsideHandler);
  },

  removeSearchClickOutsideListener() {
    if (this.searchClickOutsideHandler) {
      document.removeEventListener('click', this.searchClickOutsideHandler);
      this.searchClickOutsideHandler = null;
    }
  },

  handleSearchInput() {
    if (window.gallerySearchService && this.search_query !== undefined) {
      window.gallerySearchService.handleSearchInput(this.search_query);
    }
  },

  clearSearch() {
    this.search_query = '';
    if (window.gallerySearchService) {
      window.gallerySearchService.clearSearch();
    }
  },

  openFilterModal() {
    this.$store.galleryUI.filter_modal_open = true;

    setTimeout(() => {
      const titleEl = document.querySelector('.boost-sd__filter-tree-vertical-toolbar-middle-new');
      if (titleEl) {
        titleEl.textContent = "Filter Artwork";
      }

      const isMobile = window.innerWidth <= 600;

      // Add close button only on mobile
      if (isMobile) {
        const contentEl = document.querySelector('.boost-sd__filter-tree-vertical-content');
        if (contentEl && !contentEl.querySelector('.gallery-filter-close-btn')) {
          const closeBtn = document.createElement('button');
          closeBtn.className = 'gallery-filter-close-btn';
          closeBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4L12 12" stroke="black" stroke-linecap="round"/>
            <path d="M12 4L4 12" stroke="black" stroke-linecap="round"/>
          </svg>`;
          closeBtn.addEventListener('click', () => this.closeFilterModal());
          contentEl.appendChild(closeBtn);
        }
      }
    }, 100);
  },

  closeFilterModal() {
    this.$store.galleryUI.filter_modal_open = false;

    if (this.isMobile && this.isMobile()) {
      this.refreshMobileCarousel();
    }
  },

  updateMobileCarouselOrientation() {
    // Prevent duplicate updates using a global flag
    if (window._mobileCarouselOrientationUpdating) {
      return;
    }
    window._mobileCarouselOrientationUpdating = true;

    // Clear flag after a short delay
    setTimeout(() => {
      window._mobileCarouselOrientationUpdating = false;
    }, 500);

    const footerEl = document.querySelector('.gallery-builder__footer');
    if (!footerEl) {
      return;
    }

    const carouselEl = footerEl.querySelector('.gallery-builder__mobile-carousel-track');
    if (!carouselEl) {
      return;
    }

    const $carousel = jQuery(carouselEl);

    // Only update if carousel is initialized
    if (!$carousel.hasClass('slick-initialized')) {
      return;
    }

    const newSlidesToShow = 5;

    // Find currently selected artwork before destroying
    const displayedArtworks = this.displayed_artworks;
    const selectedArtwork = displayedArtworks[0];
    let initialSlideIndex = 0;

    if (selectedArtwork) {
      const artworks = this.$store.galleryProducts?.filtered_artworks || [];
      const selectedIndex = artworks.findIndex(a => a?.id === selectedArtwork.id);
      if (selectedIndex !== -1) {
        initialSlideIndex = selectedIndex;
      }
    }

    // Destroy and recreate carousel with new products
    try {
      $carousel.slick('unslick');

      // Clear and recreate slides with new orientation products
      carouselEl.innerHTML = '';
      this.createMobileCarouselSlides();

      // Reinitialize with new settings
      $carousel.slick({
        infinite: true,
        centerMode: true,
        centerPadding: '0px',
        slidesToShow: newSlidesToShow,
        slidesToScroll: 1,
        initialSlide: initialSlideIndex,
        focusOnSelect: true,
        arrows: false,
        dots: false,
        draggable: true,
        swipe: true,
        swipeToSlide: true,
        touchMove: true,
        touchThreshold: 10,
        speed: 300,
        cssEase: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        useTransform: true,
        variableWidth: false,
        accessibility: false,
        edgeFriction: 0.15
      });

      this.initializeSwymWishlist();
    } catch (error) {
      console.error('[MOBILE-CAROUSEL] Error updating carousel orientation:', error);
    }
  },

  /**
   * Refresh mobile carousel with new filtered products
   * Includes debouncing and state checks to prevent unnecessary refreshes
   *
   * @returns {void}
   */
  refreshMobileCarousel() {

    // Guard clause: Skip during initial page load
    if (!this.$store.galleryProducts?.initial_load_complete) {
      return;
    }

    // Guard clause: Skip if carousel hasn't been initialized yet
    if (!this.mobileCarouselInitialized) {
      return;
    }

    // Debounce multiple rapid refresh calls to prevent performance issues
    if (this.mobileCarouselRefreshTimeout) {
      clearTimeout(this.mobileCarouselRefreshTimeout);
    }

    this.mobileCarouselRefreshTimeout = setTimeout(() => {
      this._doRefreshMobileCarousel();
    }, 200); // 200ms debounce for optimal UX
  },

  /**
   * Internal method to perform the actual carousel refresh
   * Handles destruction of existing carousel and re-initialization
   *
   * @private
   * @returns {void}
   */
  _doRefreshMobileCarousel() {
    // Guard clause: Prevent concurrent refreshes
    if (this.mobileCarouselInitializing) {
      return;
    }

    // Find carousel elements
    const footerEl = document.querySelector('.gallery-builder__footer');
    if (!footerEl) {
      return;
    }

    const carouselEl = footerEl.querySelector('.gallery-builder__mobile-carousel-track');
    if (!carouselEl) {
      return;
    }

    // Safely destroy existing carousel if initialized
    const $carousel = jQuery(carouselEl);
    if ($carousel.hasClass('slick-initialized')) {
      try {
        // Use unslick method for cleaner destruction
        $carousel.slick('unslick');
      } catch (error) {
        console.warn('[MOBILE-CAROUSEL] Error during destruction (non-fatal):', error.message);
      }

      // Clear all existing slides to ensure clean state
      carouselEl.innerHTML = '';
    }

    // Reset initialization flags
    this.mobileCarouselInitialized = false;
    this.mobileCarouselInitializing = false;

    // Wait for DOM cleanup before re-initializing
    // This prevents race conditions with Slick's internal cleanup
    setTimeout(() => {
      this.initMobileCarousel();
    }, 100);
  },

  // Get mobile works count display
  get mobile_works_count() {
    // Use the same count as desktop for consistency
    return this.$store.galleryProducts?.getCurrentCount() || 0;
  },

  /**
   * Initialize mobile carousel with Slick
   */
  initMobileCarousel() {
    // Only initialize on mobile
    if (!this.isMobile()) {
      return;
    }

    // Prevent multiple initializations
    if (this.mobileCarouselInitializing || this.mobileCarouselInitialized) {
      return;
    }
    this.mobileCarouselInitializing = true;

    // Wait for DOM and store to be ready
    setTimeout(() => {
      // this.$el is the gallery-builder element
      const footerEl = document.querySelector('.gallery-builder__footer');
      if (!footerEl) {
        this.mobileCarouselInitializing = false;
        setTimeout(() => this.initMobileCarousel(), 500);
        return;
      }

      const carouselEl = footerEl.querySelector('.gallery-builder__mobile-carousel-track');
      if (!carouselEl || !window.jQuery || !jQuery.fn.slick) {
        // Try again if jQuery or Slick not loaded yet
        this.mobileCarouselInitializing = false;
        setTimeout(() => this.initMobileCarousel(), 200);
        return;
      }

      // Wait for products store to be loaded
      if (!this.$store.galleryProducts?.filtered_artworks?.length) {
        this.mobileCarouselInitializing = false;
        setTimeout(() => this.initMobileCarousel(), 200);
        return;
      }

      // Check if already initialized
      if (jQuery(carouselEl).hasClass('slick-initialized')) {
        this.mobileCarouselInitialized = true;
        this.mobileCarouselInitializing = false;
        return;
      }

      // Create slides first
      this.createMobileCarouselSlides();

      // Small delay to ensure DOM is updated
      setTimeout(() => {
        // Check if slides were created
        if (carouselEl.children.length === 0) {
          this.mobileCarouselInitializing = false;
          setTimeout(() => this.initMobileCarousel(), 500);
          return;
        }

        // Keep slidesToShow constant to avoid performance issues
        const slidesToShow = 5;

        // Find the index of the selected artwork (the one currently displayed on mobile)
        let targetSlideIndex = 0;
        const displayedArtworks = this.displayed_artworks;
        const selectedArtwork = displayedArtworks[0]; // On mobile, we only display one artwork
        if (selectedArtwork) {
          const artworks = this.$store.galleryProducts?.filtered_artworks || [];
          const selectedIndex = artworks.findIndex(a => a?.id === selectedArtwork.id);
          if (selectedIndex !== -1) {
            targetSlideIndex = selectedIndex;
          }
        }

        // Initialize Slick carousel with mobile-friendly settings
        const $carousel = jQuery(carouselEl);

        // Use centerMode for proper centering with infinite scroll
        $carousel.slick({
          infinite: true,
          centerMode: true,
          centerPadding: '0px',
          slidesToShow: slidesToShow,
          slidesToScroll: 1,
          initialSlide: targetSlideIndex,
          focusOnSelect: true,
          arrows: false,
          dots: false,
          draggable: true,
          swipe: true,
          swipeToSlide: true,
          touchMove: true,
          touchThreshold: 10,
          speed: 300,
          cssEase: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          useTransform: true,
          variableWidth: false,
          accessibility: false,
          edgeFriction: 0.15
        });

        this.mobileCarouselInitialized = true;
        this.mobileCarouselInitializing = false;
        this.initializeSwymWishlist();
      }, 50);
    }, 100);
  },

  /**
   * Create mobile carousel slides dynamically
   */
  createMobileCarouselSlides() {
    // Find the carousel element in the footer
    const footerEl = document.querySelector('.gallery-builder__footer');
    if (!footerEl) return;

    const carouselEl = footerEl.querySelector('.gallery-builder__mobile-carousel-track');
    if (!carouselEl) {
      return;
    }

    // Get placeholder URLs from data attributes
    const placeholderHorizontal = carouselEl.dataset.placeholderHorizontal || '';
    const placeholderVertical = carouselEl.dataset.placeholderVertical || '';

    const artworks = this.$store.galleryProducts?.filtered_artworks || [];

    // Clear any existing slides before creating new ones
    carouselEl.innerHTML = '';

    // Create slides for each artwork
    artworks.forEach((artwork, index) => {
      if (!artwork || !artwork.id) return;

      const slideDiv = document.createElement('div');
      slideDiv.className = 'gallery-builder__mobile-carousel-slide';

      // Check if this artwork is currently selected in the main display
      const currentlyDisplayedArtworks = this.displayed_artworks;
      const isSelected = currentlyDisplayedArtworks.some(displayed => displayed?.id === artwork.id);
      if (isSelected) {
        slideDiv.classList.add('is-active');
      }

      const img = document.createElement('img');
      const thumbnailUrl = this.getThumbnailUrl(artwork);

      // Determine which placeholder to use based on artwork orientation
      const isHorizontal = this.isArtworkHorizontalOrientation(artwork);
      const placeholderUrl = isHorizontal ? placeholderHorizontal : placeholderVertical;

      // Always set real image URL to src for native lazy loading
      img.src = thumbnailUrl;

      // Use eager loading for first 10 slides, lazy for the rest
      img.loading = index < 10 ? 'eager' : 'lazy';

      // Set placeholder as onerror fallback for all slides
      if (placeholderUrl) {
        img.onerror = function() {
          this.onerror = null; // Prevent infinite loop
          this.src = placeholderUrl;
        };
      }

      img.alt = artwork.title || 'Artwork';

      slideDiv.appendChild(img);
      carouselEl.appendChild(slideDiv);

      // Add click handler after element is in DOM
      slideDiv.addEventListener('click', () => {
        this.selectArtworkFromCarousel(artwork);
        // Update active state
        this.updateMobileCarouselActiveState(artwork.id);
      });
    });
  },

  /**
   * Update mobile carousel for selected artwork (called from header selection)
   */
  updateMobileCarouselForSelectedArtwork(artworkId) {
    // Update the active state and scroll to the artwork
    this.updateMobileCarouselActiveState(artworkId);
  },

  /**
   * Update active state in mobile carousel
   */
  updateMobileCarouselActiveState(artworkId) {
    const carouselEl = document.querySelector('.gallery-builder__mobile-carousel-track');
    if (!carouselEl) return;

    // Remove active class from all slides
    carouselEl.querySelectorAll('.gallery-builder__mobile-carousel-slide').forEach(slide => {
      slide.classList.remove('is-active');
    });

    // Find the artwork and its index
    let slideIndex = -1;
    const selectedArtwork = this.$store.galleryProducts?.filtered_artworks?.find(a => a?.id === artworkId);
    if (selectedArtwork) {
      slideIndex = this.$store.galleryProducts.filtered_artworks.findIndex(a => a?.id === artworkId);
    }

    // Update the visual active state on the DOM elements
    carouselEl.querySelectorAll('.gallery-builder__mobile-carousel-slide').forEach((slide) => {
      const img = slide.querySelector('img');
      if (img && selectedArtwork) {
        const slideArtworkSrc = img.src;
        const selectedSrc = this.getThumbnailUrl(selectedArtwork);
        if (slideArtworkSrc === selectedSrc || slideArtworkSrc.includes(selectedArtwork.id)) {
          slide.classList.add('is-active');
        }
      }
    });

    // Scroll carousel to center the selected slide
    if (slideIndex !== -1) {
      try {
        const $carousel = jQuery(carouselEl);
        if ($carousel.hasClass('slick-initialized')) {
          // Go directly to the target slide
          $carousel.slick('slickGoTo', slideIndex, true);
        }
      } catch (error) {
        // Silently handle scroll errors
      }
    }
  },

  /**
   * Select artwork from mobile carousel
   */
  selectArtworkFromCarousel(artwork) {
    if (!artwork) return;

    const cleanArtwork = {
      ...artwork,
      selected_variant: undefined,
      _cached_image_url: undefined
    };

    if (this.$store.galleryCore) {
      this.$store.galleryCore.selectArtwork(cleanArtwork, this.mobile_active_artwork_index);
    }

    this.handleMobileInteraction();
    this.initializeSwymWishlist();
  }
};
